import React, { useEffect, useRef, useState } from 'react';
import { PALETTE } from '../pages/site/SiteLayout';

/**
 * HeroProductLoop, silent, auto-playing 60–90s product demo replacing the
 * static screenshot on the marketing home hero.
 *
 * Design intent:
 *   - No video file: cross-fade between existing product PNGs (fast + SEO-
 *     friendly, no autoplay/mute policies to worry about).
 *   - Pause on hover so a visitor who wants to read the screen can.
 *   - Progress dots at the bottom to signal it's a loop, not a broken image.
 *   - Fake browser chrome URL updates to match the current screen, matches
 *     the "app.keystone.cloudvoro.com/…" pattern from the previous mockup.
 *   - Respects prefers-reduced-motion, freezes on the first frame.
 *   - Preloads every image on mount so cross-fades never flash.
 *
 * Full cycle: 5 screens × 15s = 75s. Screens surface the four moments a
 * food-manufacturing buyer cares about most: planning, batch trace,
 * dispatch, recall, then a compliance/audit view to close the loop.
 */

const SLIDES = [
  {
    src: '/product/dashboard.png',
    url: 'app.keystone.cloudvoro.com / dashboard',
    label: 'Operations dashboard',
    caption: "Today's make-days, dispatches and open batches.",
    alt: 'Keystone Operations Dashboard',
  },
  {
    src: '/product/inwards-milk-intake.png',
    url: 'app.keystone.cloudvoro.com / inwards / milk-intake',
    label: 'Milk intake · goods-in',
    caption: 'Every intake is a signed batch, supplier, tanker, temp, weight.',
    alt: 'Keystone Milk Intake / Goods-In screen',
  },
  {
    src: '/product/order-entry.png',
    url: 'app.keystone.cloudvoro.com / orders / new',
    label: 'Order entry',
    caption: 'Retailer SKU codes autocomplete, no manual mapping in Excel.',
    alt: 'Keystone Order Entry screen',
  },
  {
    src: '/product/dispatches.png',
    url: 'app.keystone.cloudvoro.com / dispatches',
    label: 'Dispatch note & pallet label',
    caption: 'GS1-128 SSCC pallet label + retailer-formatted dispatch, one click.',
    alt: 'Keystone Dispatches screen',
  },
  {
    src: '/product/mock-recall.png',
    url: 'app.keystone.cloudvoro.com / recall / drill',
    label: 'Mock recall drill',
    caption: 'Forward + backward trace on a batch in under 90 seconds.',
    alt: 'Keystone Mock Recall Drill screen',
  },
  {
    src: '/product/compliance.png',
    url: 'app.keystone.cloudvoro.com / compliance',
    label: 'Audit trail · BRCGS §3.9',
    caption: "Signed audit log, what an auditor sees when they ask 'show me'.",
    alt: 'Keystone Compliance / BRCGS §3.9 audit trail screen',
  },
];

const SLIDE_MS = 5500; // ~5.5s each × 6 = 33s per full cycle (fast enough to feel alive)

export default function HeroProductLoop() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const timerRef = useRef(null);

  // Preload every slide so the cross-fade never shows a broken frame.
  useEffect(() => {
    SLIDES.forEach((s) => { const im = new window.Image(); im.src = s.src; });
  }, []);

  // Respect prefers-reduced-motion, freeze on the first frame.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const on = (e) => setReducedMotion(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', on);
    else mq.addListener(on);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', on);
      else mq.removeListener(on);
    };
  }, []);

  // Auto-advance
  useEffect(() => {
    if (paused || reducedMotion) return undefined;
    timerRef.current = setTimeout(() => {
      setActive((i) => (i + 1) % SLIDES.length);
    }, SLIDE_MS);
    return () => clearTimeout(timerRef.current);
  }, [active, paused, reducedMotion]);

  const current = SLIDES[active];

  return (
    <div
      className="hero-product"
      style={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
      }}
      data-testid="hero-product-loop"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div style={{
        position: 'relative',
        width: '100%',
        maxWidth: 720,
        borderRadius: 14,
        overflow: 'hidden',
        boxShadow: '0 40px 80px -20px rgba(10,37,64,0.45), 0 12px 30px -8px rgba(10,37,64,0.25)',
        transform: 'perspective(1800px) rotateY(-6deg) rotateX(2deg)',
        transformOrigin: 'center right',
        border: `1px solid rgba(255,255,255,0.5)`,
        background: '#fff',
      }}>
        {/* Browser chrome, URL updates per slide */}
        <div style={{
          background: '#F1ECE2',
          borderBottom: `1px solid ${PALETTE.border}`,
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#FF5F57' }} />
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#FEBC2E' }} />
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#28C840' }} />
          <div
            key={`url-${active}`}
            data-testid="hero-loop-url"
            style={{
              marginLeft: 16,
              flex: 1,
              background: '#fff',
              border: `1px solid ${PALETTE.border}`,
              borderRadius: 6,
              padding: '4px 12px',
              fontSize: 11,
              fontFamily: 'ui-monospace, SFMono-Regular, monospace',
              color: PALETTE.muted,
              letterSpacing: '0.02em',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              animation: reducedMotion ? 'none' : 'hpl-url-tick 400ms ease-out',
            }}
          >
            {current.url}
          </div>
          {!reducedMotion && (
            <span
              aria-hidden="true"
              title={paused ? 'Paused' : 'Live'}
              style={{
                marginLeft: 8,
                width: 6, height: 6,
                borderRadius: '50%',
                background: paused ? PALETTE.muted : '#28C840',
                boxShadow: paused ? 'none' : '0 0 0 0 rgba(40,200,64,0.6)',
                animation: paused ? 'none' : 'hpl-pulse 1.4s infinite',
              }}
            />
          )}
        </div>

        {/* Cross-fade image stack */}
        <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', background: '#F7F5F0' }}>
          {SLIDES.map((s, i) => (
            <img
              key={s.src}
              src={s.src}
              alt={s.alt}
              loading={i === 0 ? 'eager' : 'lazy'}
              data-testid={`hero-loop-slide-${i}`}
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%', objectFit: 'cover',
                objectPosition: 'top center',
                opacity: i === active ? 1 : 0,
                transition: reducedMotion ? 'none' : 'opacity 800ms ease-in-out',
                filter: i === active ? 'none' : 'blur(2px)',
              }}
            />
          ))}
        </div>

        {/* Progress dots (bottom overlay) */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute', left: 14, right: 14, bottom: 12,
            display: 'flex', gap: 6, justifyContent: 'center', pointerEvents: 'none',
          }}
        >
          {SLIDES.map((_, i) => (
            <span
              key={i}
              style={{
                display: 'inline-block',
                width: i === active ? 28 : 8, height: 4,
                borderRadius: 999,
                background: i === active ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.45)',
                transition: 'width 400ms ease, background 400ms ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }}
            />
          ))}
        </div>
      </div>

      {/* Floating accent caption, updates per slide */}
      <div
        key={`caption-${active}`}
        data-testid="hero-loop-caption"
        style={{
          position: 'absolute',
          bottom: -26,
          left: 0,
          background: PALETTE.navy,
          color: '#fff',
          padding: '12px 18px',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          borderRadius: 4,
          boxShadow: '0 12px 30px rgba(10,37,64,0.25)',
          maxWidth: '80%',
          animation: reducedMotion ? 'none' : 'hpl-caption-in 500ms ease-out',
        }}
      >
        <span style={{ color: PALETTE.gold }}>{current.label}</span>
        <span style={{ marginLeft: 10, opacity: 0.75, fontWeight: 500, letterSpacing: '0.02em', textTransform: 'none', fontSize: 12 }}>
          {current.caption}
        </span>
      </div>

      <style>{`
        @keyframes hpl-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(40,200,64,0.55); }
          70%  { box-shadow: 0 0 0 6px rgba(40,200,64,0);  }
          100% { box-shadow: 0 0 0 0 rgba(40,200,64,0);    }
        }
        @keyframes hpl-caption-in {
          from { transform: translateY(6px); opacity: 0; }
          to   { transform: translateY(0);   opacity: 1; }
        }
        @keyframes hpl-url-tick {
          from { transform: translateY(-2px); opacity: 0.4; }
          to   { transform: translateY(0);    opacity: 1;   }
        }
      `}</style>
    </div>
  );
}
